<template>
  <div>
    <!-- Global Loading Overlay -->
    <div 
      v-if="globalLoading.show" 
      class="global-loading-overlay"
      :class="{ 'fade-in': globalLoading.show }"
    >
      <div class="loading-content">
        <div class="spinner"></div>
        <h3 class="loading-title">{{ globalLoading.message }}</h3>
        <p class="loading-subtitle">{{ globalLoading.subtitle }}</p>
        <div v-if="globalLoading.progress > 0" class="progress-bar">
          <div class="progress-fill" :style="{ width: globalLoading.progress + '%' }"></div>
        </div>
      </div>
    </div>

    <ClientOnly>
      <div>
        <HostPanel 
        v-if="isLocalhost" 
        v-model:searchQuery="searchQuery" 
        :isLocalhost="isLocalhost" 
        :isPanelVisible="isPanelVisible" 
        :dropdownVisible="dropdownVisible" 
        :selectedOption="selectedOption" 
        :hostname="hostname" 
        :hostsData="hostsData" 
        @toggle-panel="isPanelVisible = !isPanelVisible"   
        @toggle-dropdown="dropdownVisible = !dropdownVisible" 
        @select-option="selectOption" 
        @reset-host="removeLocalhostCookie"
      />
      
      <!-- Debug Info (remove in production) -->
      <div v-if="isLocalhost" class="debug-panel-container">
        <!-- Debug Panel Toggle Button -->
        <button 
          @click="toggleDebugPanel"
          class="debug-toggle-btn"
          :class="{ 'collapsed': !isDebugPanelVisible }"
        >
          <i :class="isDebugPanelVisible ? 'fas fa-chevron-down' : 'fas fa-chevron-up'"></i>
          <span>DEBUG</span>
        </button>
        
        <!-- Debug Panel Content -->
        <div 
          v-show="isDebugPanelVisible" 
          class="debug-panel-content"
          :class="{ 'panel-visible': isDebugPanelVisible }"
        >
          <div class="font-bold text-green-400 mb-2">🔧 DEBUG INFO</div>
          
          <!-- Basic Info -->
          <div class="mb-2">
            <div class="text-yellow-300">Environment:</div>
            <div>isLocalhost: {{ isLocalhost }}</div>
            <div>hostname: {{ hostname }}</div>
            <div>pending: {{ pending }}</div>
          </div>
          
          <!-- Route Info -->
          <div class="mb-2 border-t border-gray-600 pt-2">
            <div class="text-yellow-300">Route Info:</div>
            <div>path: {{ routeInfo.path }}</div>
            <div>name: {{ routeInfo.name || 'null' }}</div>
            <div>fullPath: {{ routeInfo.fullPath }}</div>
            <div v-if="Object.keys(routeInfo.params).length > 0">
              params: {{ JSON.stringify(routeInfo.params) }}
            </div>
            <div v-if="Object.keys(routeInfo.query).length > 0">
              query: {{ JSON.stringify(routeInfo.query) }}
            </div>
            <div v-if="pageMetaData.routeName" class="text-cyan-300 text-xs">
              SEO route name: {{ pageMetaData.routeName }}
            </div>
            <div v-if="pageMetaData.mappedAt" class="text-gray-400 text-xs">
              Last mapped: {{ new Date(pageMetaData.mappedAt).toLocaleTimeString() }}
            </div>
          </div>
          
          <!-- Navigation Type Info -->
          <div class="mb-2 border-t border-gray-600 pt-2">
            <div class="text-yellow-300">Navigation Type:</div>
            <div>type: {{ navigationType }}</div>
            <div>source: {{ navigationSource }}</div>
            <div>isHardReload: {{ isHardReload }}</div>
            <div>isClientNavigation: {{ isClientNavigation }}</div>
            <div>isSSR: {{ $nuxt.ssrContext ? 'true' : 'false' }}</div>
          </div>
          
          <!-- Page Config Info -->
          <div class="mb-2 border-t border-gray-600 pt-2">
            <div class="text-yellow-300">Page Config (SEO):</div>
            <div>ssrDataLoaded: {{ ssrDataLoaded }}</div>
            <div>pageDataLoaded: {{ pageDataLoaded }}</div>
            <div>pageConfig: {{ pageConfig ? 'loaded' : 'null' }}</div>
            <div v-if="pageMetaData.source" class="text-cyan-300 text-xs">source: {{ pageMetaData.source }}</div>
            <div v-if="pageMetaData.title" class="text-green-300">title: {{ pageMetaData.title }}</div>
            <div v-if="pageTitle" class="text-lime-300 text-xs">computed title: {{ pageTitle }}</div>
            <div v-if="pageMetaData.description" class="text-green-300 text-xs">desc: {{ pageMetaData.description.substring(0, 50) }}...</div>
            <div v-if="pageMetaData.url" class="text-blue-300 text-xs">url: {{ pageMetaData.url }}</div>
            <div v-if="pageMetaData.keywords" class="text-purple-300 text-xs">keywords: {{ pageMetaData.keywords.substring(0, 30) }}...</div>
            <div v-if="pageMetaData.image" class="text-orange-300 text-xs">image: {{ pageMetaData.image.substring(0, 30) }}...</div>
            <div v-if="pageMetaData.menuTitle" class="text-pink-300 text-xs">menu: {{ pageMetaData.menuTitle }}</div>
            <div v-if="pageMetaData.parentTitle" class="text-indigo-300 text-xs">parent: {{ pageMetaData.parentTitle }}</div>
            <div class="text-red-300 text-xs">siteName: {{ siteName }}</div>
            <div class="text-yellow-200 text-xs">final title: {{ pageTitle ? `${pageTitle} / ${siteName}` : siteName }}</div>
          </div>
          
          <!-- Navigator SEO Info -->
          <div class="mb-2 border-t border-gray-600 pt-2">
            <div class="text-yellow-300">Navigator SEO:</div>
            <div>navigator.builder: {{ preConfigData?.theme?.navigator?.builder ? preConfigData.theme.navigator.builder.length + ' items' : 'null' }}</div>
            <div v-if="preConfigData?.theme?.navigator?.builder">
              <div class="text-green-300 text-xs">Available routes:</div>
              <div class="text-xs text-gray-300 max-h-16 overflow-y-auto">
                <template v-for="item in preConfigData.theme.navigator.builder.slice(0, 5)" :key="item.id">
                  <div>{{ item.target }} → {{ item.title }}</div>
                </template>
                <div v-if="preConfigData.theme.navigator.builder.length > 5" class="text-gray-400">
                  ...and {{ preConfigData.theme.navigator.builder.length - 5 }} more
                </div>
              </div>
            </div>
          </div>
          
          <!-- Hosts Info -->
          <div class="mb-2 border-t border-gray-600 pt-2">
            <div class="text-yellow-300">Hosts:</div>
            <div>hostsData: {{ hostsData ? hostsData.length : 'null' }} items</div>
            <div v-if="selectedOption && selectedOption.usageCount !== undefined">
              Usage: {{ selectedOption.usageCount }}
            </div>
            <div v-if="selectedOption && selectedOption.status">
              Status: {{ selectedOption.status }}
            </div>
          </div>
        </div>
      </div>
      
      <button  
        v-if="profile" 
        @click="toggleUserPanelFooter" 
        class="toggle-footer-btn text-xs border-t border-l border-r border-gray-200 z-50" 
        :style="{ bottom: isUserPanelVisible ? '24px' : '0', right: '0' }"
      >
        <i :class="isUserPanelVisible ? 'fas fa-chevron-down' : 'fas fa-chevron-up'"></i> user info
      </button>

      <div 
        class="user-panel-footer bg-white text-opacity-70 text-black border-t border-gray-200 fixed w-full bottom-0 z-40 text-xs px-2 py-1" 
        v-if="isUserPanelVisible && profile"
      >
        <div class="user-data-columns">
          <div class="user-data-column">
            <p><i class="fas fa-user"></i> <span class="text-xs text-gray-400">{{ profile.firstname }} {{ profile.lastname }}</span></p>
          </div>
          <div class="user-data-column">
            <p><i class="fas fa-globe"></i> <span class="text-xs text-gray-400">{{ profile.browserName }}</span></p>
          </div>
          <div class="user-data-column">
            <p><i class="fas fa-desktop text-xs"></i> <span class="text-xs text-gray-400">{{ profile.osName }}</span></p>
          </div>
        </div>
      </div>
      
      <NuxtLayout>
        <ContextMenu/>
        <NuxtPage :key="componentKey"/>
      </NuxtLayout>
      </div>
    </ClientOnly>
  </div>
</template>

<script setup>
// === IMPORTS ===
import axios from 'axios';
import { useHead, useSeoMeta, useCookie, useRouter, useRoute, reloadNuxtApp } from '#imports';
import { handleRequest, handlePreConfigRequest } from '~/utils/handle.js';
import { provide, ref, onMounted, watch, computed, defineAsyncComponent, nextTick } from 'vue';
import { useHistory } from '~/composables/useHistory';
import { useSession } from '~/composables/useSession';
import { handleFetch } from '~/utils/fetch.js';

// Lazy load components for better performance
const ContextMenu = defineAsyncComponent(() => import('~/components/plugin/ContextMenu.vue'));
const HostPanel = defineAsyncComponent(() => import('~/components/HostPanel.vue'));

// === COMPOSABLES ===
const { recordHistory } = useHistory();
const { sessionValid } = useSession();
const router = useRouter();
const route = useRoute();

// === REACTIVE STATE ===
const pending = ref(true);
const error = ref(null);
const componentKey = ref(0);
const hostname = ref(null);
const isLocalhost = ref(false);
const profile = ref(null);

// UI State
const isPanelVisible = ref(false);
const dropdownVisible = ref(false);
const selectedOption = ref(null);
const searchQuery = ref('');
const isUserPanelVisible = ref(false);
const isDebugPanelVisible = ref(false); // สำหรับ toggle debug panel - เริ่มต้นย่อไว้

// Global Loading State
const globalLoading = ref({
  show: false,
  message: 'Loading...',
  subtitle: 'Please wait',
  progress: 0
});

// Data State  
const configData = ref(null);
const hostsData = ref([]);
const themeData = ref(null);
const spaceData = ref(null);

// Route & Page-specific data for SEO
const currentRoute = ref({
  path: null,
  params: {},
  query: {},
  name: null,
  fullPath: null
});

// SSR State tracking
const ssrDataLoaded = ref(false);
const pageDataLoaded = ref(false);

// Navigation type tracking
const navigationType = ref('unknown');
const navigationSource = ref('unknown');
const isHardReload = ref(false);
const isClientNavigation = ref(false);

// Shared State (useState for SSR hydration)
const preConfigData = useState('preConfigData', () => null);
const tenantConfig = useState('tenantConfig', () => null);
const sessionData = useState('sessionData', () => null);

// SEO State (จาก middleware)
const pageConfig = useState('pageConfig', () => null);
const pageMetaData = useState('pageMetaData', () => ({
  title: null,
  description: null,
  keywords: null,
  image: null,
  url: null
}));

// === COMPUTED PROPERTIES ===
const authToken = useCookie('_account');
const authTokenValue = computed(() => !!authToken.value);

// Route information computed
const routeInfo = computed(() => ({
  path: route.path,
  name: route.name,
  params: route.params,
  query: route.query,
  fullPath: route.fullPath,
  meta: route.meta
}));

const filteredHosts = computed(() => {
  if (!searchQuery.value || !hostsData.value || !Array.isArray(hostsData.value)) {
    return hostsData.value?.slice(-14) || [];
  }
  
  const query = searchQuery.value.toLowerCase();
  return hostsData.value
    .filter(option => 
      option.siteName?.toLowerCase().includes(query) ||
      option.hostname?.toLowerCase().includes(query) ||
      option.displayName?.toLowerCase().includes(query) ||
      option.status?.toLowerCase().includes(query)
    )
    .slice(-14);
});

// === ROUTE MANAGEMENT ===
const updateCurrentRoute = () => {
  currentRoute.value = {
    path: route.path,
    params: { ...route.params },
    query: { ...route.query },
    name: route.name,
    fullPath: route.fullPath
  };
  
  console.log('[Route] Updated route info:', currentRoute.value);
};

// Detect navigation type for SSR debugging
const detectNavigationType = () => {
  if (process.server) {
    // On server, it's always initial load or hard reload
    navigationType.value = 'ssr-initial';
    navigationSource.value = 'server-side';
    isHardReload.value = true;
    isClientNavigation.value = false;
    
    console.log('[SSR] Navigation type: SSR Initial Load');
  } else if (import.meta.client && typeof window !== 'undefined') {
    // Client-side navigation detection
    const perfEntries = performance.getEntriesByType('navigation');
    
    if (perfEntries.length > 0) {
      const navEntry = perfEntries[0];
      
      if (navEntry.type === 'reload') {
        navigationType.value = 'hard-reload';
        navigationSource.value = 'browser-reload';
        isHardReload.value = true;
        isClientNavigation.value = false;
      } else if (navEntry.type === 'back_forward') {
        navigationType.value = 'browser-navigation';
        navigationSource.value = 'browser-history';
        isHardReload.value = false;
        isClientNavigation.value = true;
      } else if (navEntry.type === 'navigate') {
        navigationType.value = 'client-navigation';
        navigationSource.value = 'spa-routing';
        isHardReload.value = false;
        isClientNavigation.value = true;
      }
    } else {
      // Fallback detection
      if (window.performance.timing.loadEventStart > 0) {
        navigationType.value = 'page-load';
        navigationSource.value = 'initial-load';
        isHardReload.value = true;
        isClientNavigation.value = false;
      }
    }
    
    console.log('[Client] Navigation type detected:', {
      type: navigationType.value,
      source: navigationSource.value,
      isHardReload: isHardReload.value,
      isClientNavigation: isClientNavigation.value
    });
  }
};

// === HOSTNAME SETUP ===
const setupHostname = () => {
  // Set default hostname first
  hostname.value = 'frontend.cloudrestfulapi.com';
  
  if (process.server) {
    try {
      const nuxtApp = useNuxtApp();
      const req = nuxtApp.ssrContext?.event?.req;
      if (req) {
        hostname.value = req.headers.host?.split(':')[0] || 'frontend.cloudrestfulapi.com';
      }
    } catch (error) {
      // Silently handle SSR context issues
    }    } else if (typeof window !== 'undefined') {
      hostname.value = window.location.hostname.split(':')[0];
    }

  // Handle localhost and development environments
  if (hostname.value === 'localhost' || hostname.value === '127.0.0.1' || hostname.value.includes('frontend.dev.test') || hostname.value.includes('ngrok.app')) {
    isLocalhost.value = true;
    hostname.value = 'doa.fti.or.th';
    console.log('[Setup] Localhost detected, setting isLocalhost = true');
  }

  if (hostname.value === 'frontend.cloudrestfulapi.com') {
    hostname.value = 'fti.academy';
  }

  console.log('[Setup] Final hostname:', hostname.value, 'isLocalhost:', isLocalhost.value);

  // Check localhost cookie override
  try {
    const localhostCookie = useCookie('_localhost', { maxAge: 86400 });
    if (localhostCookie.value) {
      hostname.value = localhostCookie.value;
    }
  } catch (error) {
    // Silently handle cookie access issues
  }
};

// === API FUNCTIONS ===
const prepareCookiesForRequest = (environment) => {
  const headers = { 'Content-Type': 'application/json', 'X-Environment': environment };
  
  // Only try to access cookies in proper context
  if (process.server) {
    try {
      const nuxtApp = useNuxtApp();
      const event = nuxtApp.ssrContext?.event;
      const cookieHeader = event?.node?.req?.headers?.cookie;
      
      if (cookieHeader) {
        headers['Cookie'] = cookieHeader;
      }
    } catch (error) {
      // Silently handle cookie access issues in SSR
      // This is expected behavior in some contexts
    }
  }
  
  return headers;
};

const fetchTenantConfig = async (hostname) => {
  const startTime = Date.now();
  
  console.log(`[SSR] 🏢 Fetching tenant config for hostname: ${hostname}`);
  
  try {
    const testUrl = `https://multi-tenants.manonsanoi.workers.dev/hostname/${hostname}`;
    const headers = prepareCookiesForRequest('production');
    
    console.log(`[SSR] 🔗 Tenant config URL: ${testUrl}`);
    console.log(`[SSR] 📝 Request headers:`, headers);
    
    const fetchOptions = {
      method: 'GET',
      headers,
      credentials: 'include',
    };
    
    const response = await fetch(testUrl, fetchOptions);
    
    console.log(`[SSR] 📡 Tenant config response status: ${response.status} ${response.statusText}`);
    
    if (!response.ok) {
      throw new Error(`Tenant config fetch failed: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    tenantConfig.value = data;
    
    console.log(`[SSR] ✅ Tenant config loaded:`, data);
    
    // Set session data based on tenant response
    const sessionInfo = data.session || null;
    sessionData.value = (sessionInfo?.valid && sessionInfo?.user) 
      ? { session: true, user: sessionInfo.user }
      : { session: false };
    
    console.log(`[SSR] 👤 Session data set:`, sessionData.value);
    
    const duration = Date.now() - startTime;
    console.log(`[SSR] ⏱️ Tenant config completed in ${duration}ms`);
    
    return data;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[SSR] ❌ Tenant config failed after ${duration}ms:`, error);
    throw error;
  }
};

const fetchPreConfig = async (hostname, environment = 'production') => {
  const startTime = Date.now();
  
  console.log(`[SSR] ⚙️ Fetching pre-config for hostname: ${hostname}, env: ${environment}`);
  
  try {
    const preConfigRequest = handlePreConfigRequest(hostname, environment);
    const headers = prepareCookiesForRequest(environment);
    
    console.log(`[SSR] 🔗 Pre-config URL: ${preConfigRequest.url}`);
    console.log(`[SSR] 📝 Pre-config headers:`, { ...preConfigRequest.headers, ...headers });
    
    const fetchOptions = {
      method: preConfigRequest.method,
      headers: { ...preConfigRequest.headers, ...headers },
    };
    
    if (typeof window !== 'undefined') {
      fetchOptions.credentials = 'include';
    }
    
    const response = await fetch(preConfigRequest.url, fetchOptions);
    
    console.log(`[SSR] 📡 Pre-config response status: ${response.status} ${response.statusText}`);
    
    if (!response.ok) {
      throw new Error(`Pre-config fetch failed: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    preConfigData.value = data;
    
    console.log(`[SSR] ✅ Pre-config loaded:`, data);
    
    const duration = Date.now() - startTime;
    console.log(`[SSR] ⏱️ Pre-config completed in ${duration}ms`);
    
    return data;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[SSR] ❌ Pre-config failed after ${duration}ms for ${environment}:`, error);
    throw error;
  }
};

const fetchHostsData = async () => {
  // Only fetch hosts data for localhost environment
  if (!isLocalhost.value) {
    //console.log('[HostsData] Skipping hosts data fetch - not localhost environment');
    return { success: false, error: 'Not localhost environment' };
  }
  
  const startTime = Date.now();
  
  try {
    //console.log('[HostsData] Fetching hosts data for localhost environment...');
    const hostsUrl = 'https://multi-tenants.manonsanoi.workers.dev/hostnames';
    const headers = prepareCookiesForRequest('production');
    
    const fetchOptions = {
      method: 'GET',
      headers,
      credentials: 'include',
    };
    
    const response = await fetch(hostsUrl, fetchOptions);
    
    if (!response.ok) {
      throw new Error(`Hosts data fetch failed: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (data.success && data.hostnames && Array.isArray(data.hostnames)) {
      // Use hostnameData if available, fallback to hostnames array
      if (data.hostnameData && Array.isArray(data.hostnameData)) {
        // Transform hostnameData to include more detailed information
        hostsData.value = data.hostnameData
          .filter(hostData => hostData.hostname && hostData.hostname !== 'hostname:index')
          .map(hostData => ({
            hostname: hostData.hostname,
            siteName: hostData.name || hostData.hostname,
            token: hostData.token,
            status: hostData.status,
            usageCount: hostData.data?.usageCount || 0,
            lastUsed: hostData.data?.lastUsed,
            createdAt: hostData.createdAt,
            databaseName: hostData.data?.databaseName,
            displayName: formatSiteName(hostData.hostname)
          }));
      } else {
        // Fallback to simple hostnames array transformation
        hostsData.value = data.hostnames
          .filter(hostname => hostname && typeof hostname === 'string' && hostname !== 'hostname:index')
          .map(hostname => ({
            hostname: hostname,
            siteName: hostname,
            displayName: formatSiteName(hostname)
          }));
      }
      
      //console.log(`[HostsData] Loaded ${hostsData.value.length} hosts:`, hostsData.value);
    } else {
      //console.warn('[HostsData] Invalid response format:', data);
      hostsData.value = [];
    }
    
    const duration = Date.now() - startTime;
    
    return data;
  } catch (error) {
    const duration = Date.now() - startTime;
    //console.error(`[HostsData] Fetch failed after ${duration}ms:`, error);
    // Set empty array on error to prevent UI issues
    hostsData.value = [];
    // Don't throw error to prevent blocking the app initialization
    return { success: false, error: error.message };
  }
};

// === USER PROFILE FUNCTIONS ===
const fetchUserProfile = async () => {
  // Skip profile fetching for localhost-only environment
  //console.log('[Profile] Skipping profile fetch - localhost environment only');
  return;
};

const setupUserProfile = () => {
  if (typeof window !== 'undefined') {
    const { browserName, osName } = getBrowserAndOS();
    profile.value = { ...profile.value, browserName, osName };
  }
};

const getBrowserAndOS = () => {
  const userAgent = navigator.userAgent;
  let browserName = 'Unknown', osName = 'Unknown OS';

  // Browser detection
  if (userAgent.indexOf("Chrome") > -1) browserName = "Chrome";
  else if (userAgent.indexOf("Firefox") > -1) browserName = "Firefox";
  else if (userAgent.indexOf("Safari") > -1) browserName = "Safari";
  else if (userAgent.indexOf("MSIE") > -1 || userAgent.indexOf("Trident") > -1) browserName = "Internet Explorer";

  // OS detection
  if (userAgent.indexOf("Win") > -1) osName = "Windows";
  else if (userAgent.indexOf("Mac") > -1) osName = "MacOS";
  else if (userAgent.indexOf("X11") > -1 || userAgent.indexOf("Linux") > -1) osName = "Linux";

  return { browserName, osName };
};

// === UTILITY FUNCTIONS ===
const formatSiteName = (hostname) => {
  if (!hostname) return 'Unknown Site';
  
  // Handle thaicluster.com subdomains
  if (hostname.endsWith('.thaicluster.com')) {
    const subdomain = hostname.replace('.thaicluster.com', '');
    return `${subdomain.charAt(0).toUpperCase() + subdomain.slice(1)} - Thai Cluster`;
  }
  
  // Handle academy subdomains
  if (hostname.endsWith('.academy')) {
    const subdomain = hostname.replace('.academy', '');
    return `${subdomain.charAt(0).toUpperCase() + subdomain.slice(1)} Academy`;
  }
  
  // Handle cloudrestfulapi.com subdomains
  if (hostname.endsWith('.cloudrestfulapi.com')) {
    const subdomain = hostname.replace('.cloudrestfulapi.com', '');
    return `${subdomain.charAt(0).toUpperCase() + subdomain.slice(1)} - Cloud API`;
  }
  
  // Handle .or.th domains
  if (hostname.endsWith('.or.th')) {
    const parts = hostname.split('.');
    return parts.map(part => part.toUpperCase()).join(' ');
  }
  
  // Handle .asia domains
  if (hostname.endsWith('.asia')) {
    const subdomain = hostname.replace('.asia', '');
    return `${subdomain.toUpperCase()} Asia`;
  }
  
  // Handle .com domains
  if (hostname.endsWith('.com')) {
    const subdomain = hostname.replace('.com', '');
    return `${subdomain.charAt(0).toUpperCase() + subdomain.slice(1)}`;
  }
  
  // Default formatting - capitalize first letter and replace dots with spaces
  return hostname
    .split('.')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

// === SEO MAPPING FUNCTIONS ===
const findSEODataByRoute = (routePath, navigatorData) => {
  if (!navigatorData || !Array.isArray(navigatorData)) {
    console.log('[SEO] No navigator data available for SEO mapping');
    return null;
  }
  
  console.log('[SEO] Searching for route:', routePath, 'in', navigatorData.length, 'menu items');
  
  // Function to search in menu items and submenus recursively
  const searchInMenuItems = (items, targetPath) => {
    for (const item of items) {
      // Check main menu item
      if (item.target === targetPath && item.seo) {
        console.log('[SEO] Found direct match for route:', targetPath, 'with SEO data:', item.seo);
        return {
          ...item.seo,
          source: 'navigator-main-menu',
          menuTitle: item.title,
          menuId: item.id
        };
      }
      
      // Check submenu items
      if (item.subMenu && Array.isArray(item.subMenu)) {
        const subMenuResult = searchInMenuItems(item.subMenu, targetPath);
        if (subMenuResult) {
          console.log('[SEO] Found submenu match for route:', targetPath);
          return {
            ...subMenuResult,
            source: 'navigator-sub-menu',
            parentTitle: item.title,
            parentId: item.id
          };
        }
      }
    }
    return null;
  };
  
  const seoData = searchInMenuItems(navigatorData, routePath);
  
  if (seoData) {
    console.log('[SEO] Route SEO mapping successful:', {
      route: routePath,
      title: seoData.title,
      description: seoData.description,
      keywords: seoData.keywords,
      source: seoData.source
    });
  } else {
    console.log('[SEO] No SEO data found for route:', routePath);
  }
  
  return seoData;
};

const mapRouteToSEO = (routePath, routeName = null) => {
  const navigatorData = preConfigData.value?.theme?.navigator?.builder;
  
  if (!navigatorData) {
    console.log('[SEO] No theme.navigator.builder data available');
    return null;
  }
  
  console.log('[SEO] Mapping route:', routePath, 'with name:', routeName);
  
  const seoData = findSEODataByRoute(routePath, navigatorData);
  
  if (seoData) {
    // Create new object to ensure reactivity
    const newMetaData = {
      title: seoData.title || null,
      description: seoData.description || 'Welcome to our application',
      keywords: seoData.keywords || (seoData.keywordsList ? seoData.keywordsList.join(', ') : 'application, website'),
      image: seoData.image || '/default-og-image.jpg',
      url: `https://${hostname.value}${routePath}`,
      type: 'website',
      source: seoData.source,
      menuTitle: seoData.menuTitle,
      menuId: seoData.menuId,
      parentTitle: seoData.parentTitle,
      parentId: seoData.parentId,
      routeName: routeName,
      mappedAt: new Date().toISOString()
    };
    
    console.log('[SEO] Updating pageMetaData with:', newMetaData);
    
    // Force update by completely replacing the object
    pageMetaData.value = newMetaData;
    
    console.log('[SEO] Page meta data updated from navigator:', pageMetaData.value);
    
    // Wait a tick then force another update to trigger computed
    nextTick(() => {
      console.log('[SEO] NextTick - Force triggering computed updates');
      // Trigger computed updates by accessing the values
      const _ = {
        title: pageTitle.value,
        desc: pageDescription.value,
        keywords: pageKeywords.value
      };
      console.log('[SEO] Computed values after nextTick:', _);
    });
    
    return pageMetaData.value;
  } else {
    console.log('[SEO] No SEO data found, setting defaults for route:', routePath);
    
    // Create new default object
    const defaultMetaData = {
      title: null,
      description: 'Welcome to our application',
      keywords: 'application, website',
      image: '/default-og-image.jpg',
      url: `https://${hostname.value}${routePath}`,
      type: 'website',
      source: 'default-no-match',
      routeName: routeName,
      mappedAt: new Date().toISOString()
    };
    
    console.log('[SEO] Setting default pageMetaData:', defaultMetaData);
    
    // Force update by completely replacing the object
    pageMetaData.value = defaultMetaData;
    
    // Wait a tick then force another update
    nextTick(() => {
      console.log('[SEO] NextTick - Force triggering computed updates (defaults)');
      const _ = {
        title: pageTitle.value,
        desc: pageDescription.value,
        keywords: pageKeywords.value
      };
      console.log('[SEO] Computed values after nextTick (defaults):', _);
    });
    
    return null;
  }
};

const loadEruda = () => {
  if (typeof window !== "undefined" && !window.eruda) {
    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/eruda";
    script.async = true;
    script.onload = () => {
      if (window.eruda) {
        window.eruda.init();
        window.eruda.position({x: 10, y: 800});
      }
    };
    document.head.appendChild(script);
  }
};

const setupCookiesAndRedirect = () => {
  try {
    // Set cluster cookie if available
    if (configData.value?.clusterId) {
      const clusterCookie = useCookie('_cluster', { maxAge: 3600 });
      clusterCookie.value = configData.value.clusterId;
    }

    // Handle client-side redirect
    if (typeof window !== 'undefined') {
      const clusterCookieCheck = useCookie('_cluster');
      if (clusterCookieCheck.value && router.currentRoute.value.path === '/') {
        router.push('/cluster/user/dashboard');
      }
    }
  } catch (error) {
    // Silently handle cookie/router access issues
  }
};

const provideDataToComponents = () => {
  // Update reactive refs that are already provided
};

// === EVENT HANDLERS ===
const selectOption = (option) => {
  selectedOption.value = option;
  dropdownVisible.value = false;
  
  try {
    const localhostCookie = useCookie('_localhost', { maxAge: 86400 });
    localhostCookie.value = option.hostname;
    hostname.value = option.hostname;
    
    const hostCookie = useCookie('_site', { maxAge: 900 });
    hostCookie.value = null;
    
    location.reload();
  } catch (error) {
    console.error('Error updating cookies:', error);
  }
};

const removeLocalhostCookie = () => {
  try {
    const localhostCookie = useCookie('_localhost', { maxAge: 86400 });
    const siteCookie = useCookie('_site', { maxAge: 900 });
    localhostCookie.value = null;
    siteCookie.value = null;
    location.reload();
  } catch (error) {
    console.error('Error removing cookies:', error);
  }
};

const toggleUserPanelFooter = () => {
  isUserPanelVisible.value = !isUserPanelVisible.value;
};

const toggleDebugPanel = () => {
  isDebugPanelVisible.value = !isDebugPanelVisible.value;
};

// Watch specifically for route path changes to ensure SEO mapping
watch(() => route.path, async (newPath, oldPath) => {
  if (newPath !== oldPath) {
    console.log('[Route Path] Route path changed from:', oldPath, 'to:', newPath);
    console.log('[Route Path] Force mapping SEO data...');
    
    // Small delay to ensure everything is ready
    await nextTick();
    
    if (preConfigData.value?.theme?.navigator?.builder) {
      console.log('[Route Path] PreConfig available, force mapping now');
      const mapped = mapRouteToSEO(newPath, route.name);
      console.log('[Route Path] SEO mapping result:', mapped ? 'success' : 'no data found');
    } else {
      console.log('[Route Path] PreConfig not available, setting defaults');
      pageMetaData.value = {
        title: null,
        description: 'Welcome to our application',
        keywords: 'application, website',
        image: '/default-og-image.jpg',
        url: `https://${hostname.value}${newPath}`,
        type: 'website',
        source: 'default-path-change',
        routeName: route.name,
        mappedAt: new Date().toISOString()
      };
    }
  }
}, { immediate: true });

// Watch for route changes and update route info
watch(route, async (newRoute, oldRoute) => {
  console.log('[Route] Route changed from:', oldRoute?.path, 'to:', newRoute.path);
  console.log('[Route] Route name changed from:', oldRoute?.name, 'to:', newRoute.name);
  
  updateCurrentRoute();
  
  // Detect navigation type on route change
  if (import.meta.client && typeof window !== 'undefined') {
    // On client-side route change, it's likely SPA navigation
    navigationType.value = 'spa-navigation';
    navigationSource.value = 'nuxt-router';
    isHardReload.value = false;
    isClientNavigation.value = true;
    
    console.log('[Route] Client-side navigation detected via route change');
  }
  
  // Always try to map SEO data for new route, even if preConfig is not yet available
  console.log('[Route] 🔍 Mapping SEO data for new route:', newRoute.path);
  console.log('[Route] 📋 Route name:', newRoute.name, '- checking for specific route handling');
  
  // Force SEO mapping immediately for route changes
  let seoMapped = false;
  
  if (preConfigData.value?.theme?.navigator?.builder) {
    console.log('[Route] ✅ PreConfig available, mapping SEO data now');
    const mappedData = mapRouteToSEO(newRoute.path, newRoute.name);
    if (mappedData) {
      console.log('[Route] ✅ SEO data mapped for route change:', mappedData);
      seoMapped = true;
    }
  }
  
  if (!seoMapped) {
    console.log('[Route] ⚠️ No SEO data found for new route:', newRoute.path);
    // Force reset to default values when no SEO data found
    pageMetaData.value = {
      title: null,
      description: 'Welcome to our application',
      keywords: 'application, website',
      image: '/default-og-image.jpg',
      url: `https://${hostname.value}${newRoute.path}`,
      type: 'website',
      source: 'default-route-change',
      routeName: newRoute.name
    };
    
    console.log('[Route] 🔄 Reset pageMetaData to defaults:', pageMetaData.value);
  }
  
  // Force reactivity update for useHead
  await nextTick();
  console.log('[Route] 🔄 Force reactivity update completed for useHead');
}, { immediate: true, flush: 'post' });

watch(hostsData, (newValue) => {
  //console.log('[App] hostsData updated:', newValue);
}, { deep: true });

watch(sessionValid, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    componentKey.value += 1;
  }
});

watch(sessionData, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    // Session data has changed
  }
}, { deep: true });

// Watch for preConfigData changes and map SEO data
watch(preConfigData, (newValue, oldValue) => {
  if (newValue && newValue !== oldValue) {
    console.log('[PreConfig] preConfigData updated, mapping SEO for current route:', route.path);
    
    // Check if theme.navigator.builder data is available
    if (newValue.theme?.navigator?.builder) {
      console.log('[PreConfig] Navigator builder data available, items count:', newValue.theme.navigator.builder.length);
      
      // Map SEO data for current route
      const seoMapped = mapRouteToSEO(route.path, route.name);
      if (seoMapped) {
        console.log('[PreConfig] ✅ SEO data mapped after preConfig load:', seoMapped);
      } else {
        console.log('[PreConfig] ⚠️ No SEO data found for current route after preConfig load:', route.path);
      }
    } else {
      console.log('[PreConfig] ⚠️ No navigator builder data in preConfig');
    }
  }
}, { deep: true });

// Watch for pageMetaData changes and log SEO updates
watch(pageMetaData, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    console.log('[SEO] Page meta data updated:', newValue);
    console.log('[SEO] Reactive head values:', {
      title: pageTitle.value,
      description: pageDescription.value,
      keywords: pageKeywords.value,
      image: pageImage.value,
      url: pageUrl.value,
      type: pageType.value
    });
  }
}, { deep: true });

// Watch for profile changes and re-provide
watch(profile, (newValue) => {
  // Profile updated - no need to provide again as it's already reactive
}, { deep: true });

// === PROVIDE DATA ===
// Initial provides
provide('session', sessionData.value);
provide('configs', preConfigData.value?.config || null);
provide('theme', preConfigData.value?.theme || null);
provide('preConfig', preConfigData.value || null);
provide('tenantConfig', tenantConfig.value || null);
provide('childData', tenantConfig.value?.child || null);
provide('space', preConfigData.value?.space || null);
provide('userData', profile.value);
provide('env', isLocalhost.value ? 'localhost' : 'production');
provide('hostsData', hostsData);

console.log('[App] Initial provides set:', {
  session: sessionData.value,
  configs: preConfigData.value?.config || null,
  theme: preConfigData.value?.theme || null,
  preConfig: preConfigData.value || null,
  tenantConfig: tenantConfig.value || null,
  childData: tenantConfig.value?.child || null,
  space: preConfigData.value?.space || null,
  userData: profile.value,
  env: isLocalhost.value ? 'localhost' : 'production',
  hostsData
});

// Route and Page specific provides
provide('currentRoute', currentRoute);
provide('routeInfo', routeInfo);
provide('pageConfig', pageConfig);
provide('pageMetaData', pageMetaData);
provide('ssrDataLoaded', ssrDataLoaded);
provide('pageDataLoaded', pageDataLoaded);
provide('navigationType', navigationType);
provide('navigationSource', navigationSource);
provide('isHardReload', isHardReload);
provide('isClientNavigation', isClientNavigation);

// SEO mapping functions
provide('mapRouteToSEO', mapRouteToSEO);
provide('findSEODataByRoute', findSEODataByRoute);

// === REACTIVE PAGE HEAD SETUP ===
// Create computed values for reactive page head
const siteName = computed(() => 
  preConfigData.value?.config?.siteName || preConfigData.value?.data?.siteName || configData.value?.siteName || 'Application'
);
const siteFavicon = computed(() => 
  preConfigData.value?.config?.siteFavicon || preConfigData.value?.data?.siteFavicon || configData.value?.siteFavicon || '/nuxt-icon.png'
);

// SEO computed values from middleware - with proper reactivity
const pageTitle = computed(() => {
  // Force reactivity by accessing the whole pageMetaData object
  const metaData = pageMetaData.value;
  console.log('[SEO Computed] pageTitle computed triggered:', metaData?.title);
  
  if (metaData?.title) {
    return metaData.title;
  }
  return null; // Let titleTemplate handle it
});

const pageDescription = computed(() => {
  const metaData = pageMetaData.value;
  console.log('[SEO Computed] pageDescription computed triggered:', metaData?.description);
  return metaData?.description || 'Welcome to our application';
});

const pageKeywords = computed(() => {
  const metaData = pageMetaData.value;
  console.log('[SEO Computed] pageKeywords computed triggered:', metaData?.keywords);
  return metaData?.keywords || 'application, website';
});

const pageImage = computed(() => {
  const metaData = pageMetaData.value;
  console.log('[SEO Computed] pageImage computed triggered:', metaData?.image);
  return metaData?.image || '/default-og-image.jpg';
});

const pageUrl = computed(() => {
  const metaData = pageMetaData.value;
  console.log('[SEO Computed] pageUrl computed triggered:', metaData?.url);
  return metaData?.url || `https://${hostname.value}${route.fullPath}`;
});

const pageType = computed(() => {
  const metaData = pageMetaData.value;
  console.log('[SEO Computed] pageType computed triggered:', metaData?.type);
  return metaData?.type || 'website';
});

// Setup page head with reactive data including SEO
const headData = computed(() => {
  console.log('[SEO Head] useHead computed triggered with:', {
    title: pageTitle.value,
    siteName: siteName.value,
    mappedAt: pageMetaData.value?.mappedAt
  });
  
  return {
    title: pageTitle.value,
    titleTemplate: pageTitle.value ? `${pageTitle.value} / ${siteName.value}` : siteName.value,
  };
});

// Use reactive head data
useHead(headData);

// Setup favicon separately
useHead({
  link: [computed(() => ({
    rel: 'icon',
    type: 'image/x-icon',
    href: siteFavicon.value,
  }))]
});

// Use useSeoMeta for better SEO meta tag handling
const seoMetaData = computed(() => {
  console.log('[SEO Meta] useSeoMeta computed triggered with:', {
    title: pageMetaData.value?.title || siteName.value,
    description: pageDescription.value,
    mappedAt: pageMetaData.value?.mappedAt
  });
  
  return {
    // Basic meta tags
    description: pageDescription.value,
    keywords: pageKeywords.value,
    
    // Open Graph
    ogType: pageType.value,
    ogSiteName: siteName.value,
    ogTitle: pageMetaData.value?.title || siteName.value,
    ogDescription: pageDescription.value,
    ogImage: pageImage.value,
    ogUrl: pageUrl.value,
    ogLocale: 'th_TH',
    
    // Twitter
    twitterCard: 'summary_large_image',
    twitterTitle: pageMetaData.value?.title || siteName.value,
    twitterDescription: pageDescription.value,
    twitterImage: pageImage.value,
    
    // Additional
    robots: 'index,follow',
    author: siteName.value,
  };
});

// Use reactive SEO meta
useSeoMeta(seoMetaData);

// === MAIN INITIALIZATION ===
const initializeApp = async () => {
  console.log(`[SSR] 🚀 Starting app initialization - SSR: ${process.server}, Client: ${import.meta.client}`);
  
  try {
    setupHostname();
    console.log(`[SSR] 🏠 Hostname setup completed: ${hostname.value}, isLocalhost: ${isLocalhost.value}`);
    
    // Detect navigation type early
    detectNavigationType();
    console.log(`[SSR] 🧭 Navigation type detected: ${navigationType.value} (${navigationSource.value})`);
    
    // Update route information early
    updateCurrentRoute();
    console.log(`[SSR] 🛣️ Route info updated:`, currentRoute.value);
    
    // Log route information for SSR debugging
    console.log('[SSR] 📊 Current route summary:', {
      path: route.path,
      name: route.name,
      params: route.params,
      query: route.query,
      fullPath: route.fullPath,
      hostname: hostname.value,
      isSSR: process.server,
      navigationType: navigationType.value,
      navigationSource: navigationSource.value,
      isHardReload: isHardReload.value
    });

    // SSR Data Loading
    if (process.server) {
      console.log('[SSR] 📥 Starting SSR data loading phase...');
      try {
        const promises = [
          fetchTenantConfig(hostname.value),
          fetchPreConfig(hostname.value, isLocalhost.value ? 'localhost' : 'production'),
          // Only fetch hosts data if it's localhost environment
          ...(isLocalhost.value ? [fetchHostsData()] : [])
        ];
        
        console.log(`[SSR] 🔄 Running ${promises.length} parallel requests...`);
        await Promise.all(promises);
        
        // Mark SSR data as loaded
        ssrDataLoaded.value = true;
        console.log('[SSR] ✅ All config data loaded successfully for:', navigationType.value);
        
        // Map SEO data after preConfig is loaded (SSR)
        console.log('[SSR] 🔍 Starting SEO mapping for route:', route.path);
        const seoMapped = mapRouteToSEO(route.path, route.name);
        if (seoMapped) {
          console.log('[SSR] ✅ SEO data mapped successfully in SSR:', seoMapped);
        } else {
          console.log('[SSR] ⚠️ No SEO data found for route in SSR:', route.path);
        }
        
      } catch (preConfigError) {
        console.warn('[SSR] ⚠️ Config fetch failed, proceeding with normal workflow:', preConfigError);
      }
    } else {
      // Client-side data loading - only fetch hosts data if localhost
      if (typeof window !== 'undefined' && isLocalhost.value) {
        try {
          console.log('[Client] Fetching hosts data for localhost...');
          await fetchHostsData();
        } catch (hostsError) {
          console.warn('[Client] Hosts data fetch failed:', hostsError);
        }
      }
      
      // Map SEO data on client side after preConfig is available
      if (preConfigData.value) {
        console.log('[Client] 🔍 Starting SEO mapping for route:', route.path);
        const seoMapped = mapRouteToSEO(route.path, route.name);
        if (seoMapped) {
          console.log('[Client] ✅ SEO data mapped successfully on client:', seoMapped);
        } else {
          console.log('[Client] ⚠️ No SEO data found for route on client:', route.path);
        }
      }
    }

    // Setup configuration data
    if (preConfigData.value?.data) {
      configData.value = preConfigData.value.data;
    }

    if (preConfigData.value?.theme) {
      themeData.value = preConfigData.value.theme;
    }

    if (preConfigData.value?.space) {
      spaceData.value = preConfigData.value.space;
    }

    setupCookiesAndRedirect();
    provideDataToComponents();
    
    console.log('[SSR] 🔧 App setup completed');

    // Client-side specific setup
    if (typeof window !== 'undefined') {
      console.log('[SSR] 🖥️ Running client-side setup...');
      recordHistory();
      
      if (isLocalhost.value) {
        loadEruda();
        console.log('[SSR] 🐛 Eruda loaded for localhost');
      }
      
      setupUserProfile();
      console.log('[SSR] 👤 User profile setup completed');
    }

    console.log('[SSR] ✅ App initialization completed successfully');

  } catch (err) {
    error.value = err;
    console.error('[SSR] ❌ Initialization error:', err);
  } finally {
    pending.value = false;
    console.log('[SSR] 🏁 App initialization finished, pending set to false');
  }
};

// === GLOBAL LOADING FUNCTIONS ===
const showGlobalLoading = (message = 'Loading...', subtitle = 'Please wait') => {
  globalLoading.value = {
    show: true,
    message,
    subtitle,
    progress: 0
  };
};

const hideGlobalLoading = () => {
  globalLoading.value.show = false;
};

const setLoadingProgress = (progress) => {
  globalLoading.value.progress = progress;
};

// === NAVIGATION EVENT HANDLERS ===
const handleBeforeReload = () => {
  showGlobalLoading('Reloading Application', 'Please wait while we refresh the page...');
};

const handleNavigationStart = () => {
  showGlobalLoading('Navigating...', 'Loading page content...');
};

const handleNavigationEnd = () => {
  hideGlobalLoading();
};

// Override reloadNuxtApp to show loading
const reloadWithLoading = async () => {
  showGlobalLoading('Reloading Application', 'Please wait while we restart...');
  
  // Add a small delay to ensure loading is visible
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // Call the original reloadNuxtApp
  await reloadNuxtApp();
};

// Provide the reload function globally
provide('reloadWithLoading', reloadWithLoading);
provide('showGlobalLoading', showGlobalLoading);
provide('hideGlobalLoading', hideGlobalLoading);
provide('setLoadingProgress', setLoadingProgress);

onMounted(async () => {
  console.log('[onMounted] Starting client initialization...');
  
  // Detect navigation type on client mount
  if (import.meta.client && typeof window !== 'undefined') {
    detectNavigationType();
  }
  
  // Initialize the application on client side
  if (import.meta.client && typeof window !== 'undefined') {
    await initializeApp();
    
    // Force fetch hosts data ONLY if localhost and not already available
    if (isLocalhost.value && (!hostsData.value || hostsData.value.length === 0)) {
      console.log('[onMounted] Localhost detected - Force fetching hosts data...');
      try {
        await fetchHostsData();
        console.log('[onMounted] Hosts data loaded:', hostsData.value);
      } catch (error) {
        console.error('[onMounted] Failed to load hosts data:', error);
      }
    } else if (isLocalhost.value) {
      console.log('[onMounted] Localhost - Hosts data already available:', hostsData.value.length, 'hosts');
    } else {
      console.log('[onMounted] Not localhost - skipping hosts data fetch');
    }
  }
  
  // Handle localhost cookie selection - only if localhost
  if (isLocalhost.value) {
    try {
      const localhostCookie = useCookie('_localhost', { maxAge: 86400 });
      if (localhostCookie.value && hostsData.value) {
        const optionFromCookie = hostsData.value.find(host => host.hostname === localhostCookie.value);
        if (optionFromCookie) {
          selectedOption.value = optionFromCookie;
        }
      }
    } catch (error) {
      // Silently handle localhost cookie access issues
    }
  }

  // Ensure user profile is set up (without fetching)
  setupUserProfile();

  // Setup navigation loading listeners (client-side only)
  if (typeof window !== 'undefined') {
    // Listen for beforeunload (page refresh/close)
    window.addEventListener('beforeunload', handleBeforeReload);
    
    // Note: Removed router navigation listeners to avoid conflicts with navigateTo
    // navigateTo and router.push are handled by Nuxt's built-in loading indicators
    // Only manual reloads and hard refreshes will show the global loading overlay
  }
});

// Initialize for SSR - wrapped in try-catch
if (process.server) {
  try {
    await initializeApp();
  } catch (error) {
    // Silently handle SSR initialization issues
    pending.value = false;
  }
}
</script>

<style >

.ProseMirror:focus {
    outline: none;
  }

  .ProseMirror {
    font-family: inherit !important; /* Inherit the font-family from the parent */
  }
  
</style>

<style scoped>

a:hover{
  cursor: pointer;
}

/* Global Loading Overlay Styles */
.global-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  transition: opacity 0.3s ease-in-out;
}

.global-loading-overlay.fade-in {
  opacity: 1;
}

.loading-content {
  background: white;
  padding: 2rem 3rem;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  min-width: 300px;
  max-width: 400px;
  text-align: center;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
}

.loading-subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: #6b7280;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  border-radius: 3px;
  transition: width 0.3s ease;
}

/* global.css */
#loading-overlay {
  transition: opacity 0.3s ease;
}

.user-data-columns {
  display: flex;
  justify-content: space-between; /* จัดเรียงคอลัมน์ให้ห่างกัน */
}

.user-data-column {
  flex: 1; /* ให้แต่ละคอลัมน์มีขนาดเท่ากัน */
  text-align: center; /* จัดข้อความให้อยู่กลาง */
}

.toggle-footer-btn {
  position: fixed;
  bottom: 0;
  right: 0;
  padding: 5px 10px 5px 10px;
  background-color: #ffffff;
  color: rgb(80, 80, 80);
  border-radius: 7px 7px 0px 0px;
  cursor: pointer;
}

/* Debug Panel Styles */
.debug-panel-container {
  position: fixed;
  bottom: 20px;
  left: 4px;
  z-index: 50;
  max-width: 28rem;
  max-width: calc(100vw - 120px); /* เว้นที่สำหรับ user panel */
}

.debug-toggle-btn {
  background: rgba(0, 0, 0, 0.8);
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px 6px 0 0;
  cursor: pointer;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  min-width: 80px;
  justify-content: center;
}

.debug-toggle-btn:hover {
  background: rgba(0, 0, 0, 0.9);
  transform: translateY(-1px);
}

.debug-toggle-btn.collapsed {
  border-radius: 6px;
}

.debug-toggle-btn i {
  transition: transform 0.3s ease;
}

.debug-panel-content {
  background: rgba(0, 0, 0, 0.8);
  color: white;
  font-size: 0.75rem;
  padding: 8px;
  border-radius: 0 6px 6px 6px;
  max-height: 24rem;
  overflow-y: auto;
  transition: all 0.3s ease;
  opacity: 1;
  transform: translateY(0);
}

.debug-panel-content:not(.panel-visible) {
  opacity: 0;
  transform: translateY(-10px);
}

/* Custom scrollbar for debug panel */
.debug-panel-content::-webkit-scrollbar {
  width: 4px;
}

.debug-panel-content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
}

.debug-panel-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
}

.debug-panel-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

</style>
